import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import IGetVendorSourcesDTO from "../../../models/DTO/IGetVendorSourcesDTO";
import IOnGetVendorSourcesDTO from "../../../models/DTO/IOnGetVendorSourcesDTO";
import IOnProcessPurchaseOrderDTO from "../../../models/DTO/IOnProcessPurchaseOrderDTO";
import IProcessPurchaseOrderDTO from "../../../models/DTO/IProcessPurchaseOrderDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import INewPurchase_Order from "../../../models/INewPurchase_Order";
import INotification from "../../../models/INotification";
import IPurchase_Order from "../../../models/IPurchase_Order";
import IVendor_Source from "../../../models/IVendor_Source";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import AddressBlock from "../../shared/AddressBlock/AddressBlock";
import OwButton from "../../shared/OwButton/OwButton";
import OwCheckbox from "../../shared/OwCheckbox/OwCheckbox";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwTitle from "../../shared/OwTitle/OwTitle";
import "./PurchaseOrderSidebar.scss";
import Typeahead from "../../shared/Typeahead/Typeahead";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";
import OwTextArea from "../../shared/OwTextArea/OwTextArea";

type Props = {
    cancelFn: (refresh?: boolean) => void;
    purchaseOrder?: IPurchase_Order;
};

export const PurchaseOrderSidebar = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);
    const history = useNavigate();

    const [purchaseOrder, setPurchaseOrder] = useState<IPurchase_Order | INewPurchase_Order>(
        props.purchaseOrder ? props.purchaseOrder : ({ Ship_To: {} } as INewPurchase_Order)
    );
    const [isSaveActive, setIsSaveActive] = useState<boolean>(false);
    const [vendorSources, setVendorSources] = useState<IVendor_Source[]>([]);

    const processPurchaseOrder = () => {
        const payload: IProcessPurchaseOrderDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            purchaseOrder: {
                ...purchaseOrder,
            },
        };

        socket.emit(EndpointsEnum.PurchaseOrderProcessPurchaseOrder, payload);
    };

    const onProcessPurchaseOrder = (data: IOnProcessPurchaseOrderDTO) => {
        const notification: INotification = {
            message: "successfully_processed_purchase_order",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);

        props.cancelFn(true);

        history(`/purchase_orders/${data.purchaseOrder.guid}`);
    };

    const getVendorSources = (name?: string) => {
        let queryString: string | undefined;

        if (name) {
            queryString = `?name=${name}`;
        }

        const payload: IGetVendorSourcesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            queryString,
            limit: 100,
            offset: 0,
            callMeta: {
                view: "PurchaseOrderSidebar",
            },
        };

        socket.emit(EndpointsEnum.PurchaseOrderGetVendorSources, payload);
    };

    const onGetVendorSources = (data: IOnGetVendorSourcesDTO) => {
        if (data.callMeta?.view === "PurchaseOrderSidebar") {
            setVendorSources(data.vendorSources);
        }
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.PurchaseOrderOnProcessPurchaseOrder, fn: onProcessPurchaseOrder },
        { endpoint: EndpointsEnum.PurchaseOrderOnGetVendorSources, fn: onGetVendorSources },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    useEffect(() => {
        if (isSaveActive) {
            processPurchaseOrder();
        }
    }, [isSaveActive]);

    const cancel = () => {
        props.cancelFn(true);
    };

    const save = () => {
        setIsSaveActive(true);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        const _purchaseOrder = { ...purchaseOrder };

        // @ts-expect-error
        _purchaseOrder[name] = value;

        setPurchaseOrder(_purchaseOrder);
    };

    const handleAddressChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        const _purchaseOrder = { ...purchaseOrder };

        // @ts-expect-error
        _purchaseOrder.Ship_To[name] = value;

        setPurchaseOrder(_purchaseOrder);
    };

    const useDefaultShipTo = () => {
        const _purchaseOrder = { ...purchaseOrder };

        _purchaseOrder.Ship_To = {
            ...supplierModel.Ship_To,
        };

        setPurchaseOrder(_purchaseOrder);
    };

    const selectVendorSource = (vendorSource: IVendor_Source) => {
        const vendor_source_id = vendorSource.id;
        const _purchaseOrder = { ...purchaseOrder };
        _purchaseOrder.vendor_source_id = vendor_source_id;
        setPurchaseOrder(_purchaseOrder);
    };

    return (
        <div className="PurchaseOrderSidebar p-2">
            <div className={"row"}>
                <div className={"col-12"}>
                    <div className="text-end">
                        <button className="btn btn-danger btn-sm" onClick={cancel}>
                            <i className="far fa-times" />
                        </button>
                    </div>
                    <OwTitle className={"mb-2"} leftIconName={"fad fa-th-list"} title={"purchase_order"} />
                    <hr />
                </div>
            </div>

            <div>
                <div>
                    <OwLabel title={"vendor_sources"} noBottomMargin={true} />
                    <Typeahead
                        isSelectableFn={() => true}
                        typeaheadApiFn={getVendorSources}
                        typeaheadItems={vendorSources}
                        titleProp={"name"}
                        selectedItemFn={selectVendorSource}
                    />
                </div>

                <div className={"mt-5"}>
                    <div className={"mb-2"}>
                        <OwLabel title={"ship_to"} noBottomMargin={true} className={"me-2"} />
                        <OwButton title={"use_default_ship_to"} fn={useDefaultShipTo} />
                    </div>

                    <AddressBlock address={purchaseOrder.Ship_To} fn={handleAddressChange} showCompanyName={true} showName={true} />
                </div>

                <div className={"mt-2"}>
                    <OwLabel title={"po_no"} noBottomMargin={true} />
                    <OwInputWrapper name={"po_no"} fn={handleChange} value={purchaseOrder.po_no} />
                </div>

                <div className={"mt-2"}>
                    <OwLabel title={"internal_note"} noBottomMargin={true} />
                    <OwTextArea name={"internal_note"} fn={handleChange} value={purchaseOrder.internal_note} />
                </div>

                <div className={"mt-2"}>
                    <OwCheckbox
                        name={"req_approval"}
                        label={"requires_approval"}
                        checked={purchaseOrder.req_approval}
                        fn={handleChange}
                        truthyVal={true}
                        falsyVal={false}
                    />
                </div>
            </div>

            <div className={"mt-2"}>
                <OwButton title={"save"} fn={save} active={isSaveActive} disableOnActive={true} />
            </div>
        </div>
    );
};

export default PurchaseOrderSidebar;
